import { ServerError } from '@apollo/client';
import type { InferGetServerSidePropsType, Redirect } from 'next';

import { SubjectContainer } from '#components/TagPage';
import type { TagPageQuery } from '#components/__generated__/TagPage.generated';
import { TagPageDocument } from '#components/__generated__/TagPage.generated';
import { notNullOrUndefined } from '#cutils/misc/notNullOrUndefined';
import type { CacheTag } from '#cutils/page-context/edge-cache-tags';
import { createDefaultGetServerSideProps } from '#cutils/page-context/server-side-props';
import { throwOnGraphQLSemanticErrorForPaths } from '#sutils/graphql-error-matcher';
import { logger } from '#utils/logging/logger';
import { extractSlugAndIdFromPathSegment } from '#utils/pathUtils';

type TagPageType = NonNullable<NonNullable<TagPageQuery['tagPageById']>['nodes'][number]>;
type RedirectCandidates = NonNullable<TagPageQuery['tagPageBySlugOrRowId']>['nodes'];

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

const TAG_PAGE_PATH = ['tagPageById', 'nodes', 0];

function TagPage({ tagPage }: Props) {
	return <SubjectContainer tagPage={tagPage} />;
}

export const getServerSideProps = createDefaultGetServerSideProps(async (ctx, apolloClient, withCacheTags) => {
	const parsedParams = extractSlugAndIdFromPathSegment(ctx.params?.thema);

	const slug = parsedParams.slug ? `/${parsedParams.slug}` : '';
	const rowId = parsedParams.id ? parsedParams.id : '';

	try {
		const { data, error } = await apolloClient.query({
			query: TagPageDocument,
			variables: { themenGuid: rowId, themenSlug: slug, withCacheTags },
		});

		// if there is an error when retrieving the page throw it
		// note that there can't be an error in case we need to redirect as the page will not be found
		throwOnGraphQLSemanticErrorForPaths([TAG_PAGE_PATH], error);

		if (ServerError.is(error)) {
			throw error;
		}

		// Extract tag page from the query result
		const tagPage = data?.tagPageById?.nodes[0];

		if (!tagPage || `${tagPage.slug},${tagPage.rowId}` !== `${slug},${rowId}`) {
			// there is is not tag page with the correct slug and id combination
			// try to redirect to correct page if one available
			const { search } = new URL(ctx.resolvedUrl, 'http://localhost');
			return tryRedirect(data?.tagPageBySlugOrRowId?.nodes ?? [], rowId, slug, search);
		} else {
			// Check if tag page is valid
			if (!isTagValid(tagPage)) {
				return { notFound: true };
			}

			const navigationTitle = tagPage.tagByTagId?.text ?? '';

			const cacheTags = new Set<CacheTag>();
			if (withCacheTags) {
				for (const tag of tagPage.withCache ?? []) {
					cacheTags.add(tag as CacheTag);
				}
			}

			return {
				props: {
					tagPage,
					navigationTitle,
					isBreadcrumbHeadline: false,
					cacheTags,
				},
			};
		}
	} catch (error: any) {
		logger.error(error, `Error resolving themen page`);
		throw error;
	}
});

/**
 * Check if the requested page is servable as a tag page.
 * @param tagPage
 * @returns
 */
function isTagValid(tagPage: TagPageType) {
	if (!tagPage.tagByTagId?.text) {
		logger.warn(`The Tag Page has no valid Tag assigned. Board RowId: %s`, tagPage.rowId);
		return false;
	}

	return true;
}

/**
 * Generates the correct Tag Page url path from matched tag page by id and another matching by slug.
 * @param tagPage
 * @returns
 */
function tryRedirect(
	searchResults: RedirectCandidates,
	themenGuid: string,
	themenSlug: string,
	search: string
): { redirect: Redirect } | { notFound: true } {
	const candidates = searchResults.filter(notNullOrUndefined);

	// first search by rowId
	const resultForRowId = candidates.find(({ rowId }) => rowId === themenGuid);

	if (resultForRowId) {
		return {
			redirect: {
				destination: `/themen${resultForRowId.slug},${resultForRowId.rowId}${search}`,
				permanent: true,
			},
		};
	}

	// then search by slug and return the first match if found
	const resultForSlug = candidates.find(({ slug }) => slug === themenSlug);
	if (resultForSlug) {
		return {
			redirect: {
				destination: `/themen${resultForSlug.slug},${resultForSlug.rowId}${search}`,
				permanent: true,
			},
		};
	}

	if (themenGuid || themenSlug) {
		logger.warn(`Themen page for id '%s' not found`, themenGuid || themenSlug);
	}

	// if nothing is found the page does not exist
	return { notFound: true };
}

export default TagPage;
